"use client";

import Link from "next/link";
import { ArrowRight, LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface RelatedItem {
  name: string;
  href: string;
  body: string;
  icon: LucideIcon;
  accent: string;
}

export interface RelatedSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  capabilities: RelatedItem[];
  engines: RelatedItem[];
  services: RelatedItem[];
  accent: string;
}

export function RelatedSection({
  id = "related-capabilities",
  eyebrow,
  title,
  capabilities,
  engines,
  services,
  accent,
}: RelatedSectionProps) {
  return (
    <Section id={id} className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        {capabilities.length > 0 && (
          <Reveal delay={120}>
            <div className="mt-12">
              <div className="eyebrow-tiny text-[var(--color-crimson)] mb-4">Related Capabilities</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {capabilities.map((c) => {
                  const Icon = c.icon;
                  return (
                    <Link
                      key={c.name}
                      href={c.href}
                      className="glass rounded-2xl p-5 h-full block hover:scale-[1.01] transition-transform"
                      style={{
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.accent} 14%, transparent)`,
                      }}
                    >
                      <div className="flex items-start gap-3 mb-2">
                        <span
                          className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                          style={{
                            background: `color-mix(in oklch, ${c.accent} 18%, transparent)`,
                            boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.accent} 35%, transparent)`,
                          }}
                        >
                          <Icon className="h-4 w-4" style={{ color: c.accent }} />
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-bold">{c.name}</h3>
                            <ArrowRight className="h-3.5 w-3.5" style={{ color: c.accent }} />
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{c.body}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}

        {engines.length > 0 && (
          <Reveal delay={220}>
            <div className="mt-10">
              <div className="eyebrow-tiny text-[var(--color-amber)] mb-4">Related Engines</div>
              <div className="grid sm:grid-cols-3 gap-4">
                {engines.map((e) => {
                  const Icon = e.icon;
                  return (
                    <Link
                      key={e.name}
                      href={e.href}
                      className="glass rounded-2xl p-5 h-full block hover:scale-[1.01] transition-transform"
                      style={{
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${e.accent} 14%, transparent)`,
                      }}
                    >
                      <Icon className="h-5 w-5 mb-2" style={{ color: e.accent }} />
                      <h3 className="text-sm font-bold">{e.name}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{e.body}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}

        {services.length > 0 && (
          <Reveal delay={320}>
            <div className="mt-10">
              <div className="eyebrow-tiny text-[var(--color-emerald)] mb-4">Related Services</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((s) => {
                  const Icon = s.icon;
                  return (
                    <Link
                      key={s.name}
                      href={s.href}
                      className="glass rounded-2xl p-5 h-full block hover:scale-[1.01] transition-transform"
                      style={{
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${s.accent} 14%, transparent)`,
                      }}
                    >
                      <div className="flex items-start gap-3 mb-2">
                        <span
                          className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                          style={{
                            background: `color-mix(in oklch, ${s.accent} 18%, transparent)`,
                            boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${s.accent} 35%, transparent)`,
                          }}
                        >
                          <Icon className="h-4 w-4" style={{ color: s.accent }} />
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-bold">{s.name}</h3>
                            <ArrowRight className="h-3.5 w-3.5" style={{ color: s.accent }} />
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{s.body}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}
      </Container>
    </Section>
  );
}
